import { TabItem, Tabs } from '@bridebook/aura';
import { Box } from '@bridebook/ui';
import { CSSProperties } from 'fela';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { Component, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sticky, StickyProps } from 'react-sticky';
import componentStyles, {
  STICKY_BASE_CLASS_NAME,
  STICKY_STICKIED_CLASS_NAME,
} from './sticky-navigation-bar.style';

type TTabsProvided<T extends string> = Array<{ id: T; text: string }>;

interface IProps<TTabIds extends string>
  extends Pick<StickyProps, 'disableCompensation' | 'topOffset' | 'stickyStyle'> {
  /**
   * Whether the Sticky component can have `topOffset` and `stickyStyle` passed to it.
   * @default true
   */
  allowOverrides?: boolean;
  /**
   * Minimum time (in ms) between scroll event calls being fired due to throttling.
   * @default 100
   */
  delayBetweenScrollEvents?: number;
  /**
   * CSS `top` property value of the header.
   *
   * If set, will use the !important flag and override other `top` property sources.
   *
   * @default 0
   */
  headerTopStickOffset?: number;
  /**
   * Duration (in ms) where scroll events are ignored due to navigating via tabs.
   * @default 750
   */
  ignoreScrollEventsAfterNavigationDuration?: number;
  /**
   * Whether the `stickyStyle` should recalculate offset when nearing edges of the container.
   * Effectively adds a hiding animation as the sticky slides out of view.
   *
   * If `stickyStyle` is not provided, this has no effect.
   * @default false
   */
  manuallyCalculateOffset?: boolean;
  /**
   * Function called whenever a tab is clicked. Does not affect tabs' ability to navigate.
   * @param id `id` of the element the tab leads to.
   */
  onClick?: (id: TTabIds) => void;
  /**
   * Function called whenever the active tab changed.
   *
   * @param id `id` of the element the tab leads to.
   */
  onActiveTabChange?: (id: TTabIds, wasNavigationBarUsed: boolean) => void;
  /**
   * Distance (in px) from the next element at which we set aforementioned element as active.
   * @default 0
   */
  tabActivationThreshold?: number;
  /**
   * The height of the navigation bar and its tabs.
   * @default 36
   */
  tabHeight?: number;
  /**
   * Prefix added to target element's id that determines navigation tab's id.
   * @default "sticky-tab-"
   */
  tabIdPrefix?: string;
  /**
   * Y offset (in px) applied to final scroll position when navigating using header tabs.
   * @default 0
   */
  tabNavigationOffset?: number;
  /**
   * Tabs to render in the sticky navigation bar.
   */
  tabs: TTabsProvided<TTabIds>;
}

export function StickyNavigationBar<T extends string>({
  allowOverrides = true,
  delayBetweenScrollEvents = 100,
  disableCompensation,
  headerTopStickOffset = 0,
  ignoreScrollEventsAfterNavigationDuration = 750,
  manuallyCalculateOffset,
  onActiveTabChange,
  onClick,
  stickyStyle: stickyStyleBase,
  tabActivationThreshold = 0,
  tabHeight = 36,
  tabIdPrefix = 'sticky-tab-',
  tabNavigationOffset = 0,
  tabs,
  topOffset,
}: IProps<T>): ReturnType<FC<IProps<T>>> {
  const [activeTab, setActiveTab] = useState<T | null>(null);
  const [isProgramaticScroll, setIsProgramaticScroll] = useState<boolean>(false);
  const styles = componentStyles({ top: headerTopStickOffset });
  const wasNavigationBarUsed = useRef<boolean>(false);
  const getTabId = useCallback((id: T) => tabIdPrefix + id, [tabIdPrefix]);
  const getNearestTab = useCallback(
    () =>
      tabs.find(
        (tab) =>
          (document.getElementById(tab.id)?.getBoundingClientRect().bottom || 0) >
          tabActivationThreshold,
      ),
    [tabActivationThreshold, tabs],
  );

  const debouncedActiveTabChange = useMemo(
    () =>
      debounce((tab: T, wasNavigationBarUsed: boolean) => {
        onActiveTabChange?.(tab, wasNavigationBarUsed);
      }, ignoreScrollEventsAfterNavigationDuration),
    [ignoreScrollEventsAfterNavigationDuration, onActiveTabChange],
  );

  useEffect(() => {
    if (activeTab && !isProgramaticScroll) {
      debouncedActiveTabChange(activeTab, wasNavigationBarUsed.current);
      if (wasNavigationBarUsed.current) {
        wasNavigationBarUsed.current = false;
      }
    }
    return () => {
      debouncedActiveTabChange.cancel();
    };
  }, [activeTab, isProgramaticScroll, debouncedActiveTabChange]);

  useEffect(() => {
    // bail if we are in the time window after using header navigation
    // to prevent from setting each tab along the way as active and scrolling the tabs one by one
    if (isProgramaticScroll) return;
    const onScrollHandler = throttle((_event: Event) => {
      // bail if the header is not stickied to top
      if (!document.getElementsByClassName(STICKY_STICKIED_CLASS_NAME).length) {
        // as we are out of bounds, unset the active tab
        setActiveTab(null);
        return;
      }

      const nearestTab = getNearestTab();
      if (nearestTab) {
        setActiveTab(nearestTab.id);
      }
    }, delayBetweenScrollEvents);
    window.addEventListener('scroll', onScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', onScrollHandler);
  }, [delayBetweenScrollEvents, getNearestTab, getTabId, isProgramaticScroll, tabs]);

  // This has to be shared between tab navigation calls, otherwise the override might end early due to a queued call.
  const endManualNavigationScrollingOverride = useMemo(
    () =>
      debounce(() => {
        setIsProgramaticScroll(false);
      }, ignoreScrollEventsAfterNavigationDuration),
    [ignoreScrollEventsAfterNavigationDuration],
  );

  const handleClick = (id: T) => {
    setIsProgramaticScroll(true);
    wasNavigationBarUsed.current = true;
    const elementTopOffset = document.getElementById(id)?.getBoundingClientRect()?.top ?? 0;
    const elementYPositionWithVisibleHeader =
      elementTopOffset + window.scrollY - tabHeight + tabNavigationOffset;
    window.scrollTo({ top: elementYPositionWithVisibleHeader, behavior: 'smooth' });
    setActiveTab(id);
    onClick?.(id);
    endManualNavigationScrollingOverride();
  };

  // Overriding the sticky style causes animations to not work.
  // This is due to how Sticky handles the top value offset internally, as it applies and updates a style to the component.
  // As sticky listens to a lot of events, including scroll: https://github.com/captivationsoftware/react-sticky/blob/5.0.8/src/sticky.js#L44
  // It performs computation to decide on the top offset and adjusts it by passing it as a style to the wrapper
  // https://github.com/captivationsoftware/react-sticky/blob/5.0.8/src/sticky.js#L202
  // We have to replicate and override with slightly adjusted logic
  const stickyRef = useRef<Component<StickyProps, any, any>>(null);
  const [stickyStyle, setStickyStyle] = useState<CSSProperties>(() => stickyStyleBase);
  useEffect(() => {
    if (!stickyStyleBase) return;

    const onScrollHandler = (_event: Event) => {
      // bail offset calculation if not stickied
      if (!stickyRef.current) return;
      const { containerOffset, distanceFromBottom, height } = stickyRef.current.state || {};

      // If we're nearing the edge and we should pull the navbar up as means of navigation
      if (distanceFromBottom >= 0 && distanceFromBottom < containerOffset + height) {
        // adjusted Sticky top calculation logic, original: https://github.com/captivationsoftware/react-sticky/blob/5.0.8/src/sticky.js#L202
        setStickyStyle({
          ...stickyStyleBase,
          top: distanceFromBottom - containerOffset - height,
        });
      } else {
        // We want to use the maximum offset we need.
        // set constant with existing reference to pass the equality check and bail updating state
        setStickyStyle(stickyStyleBase);
      }
    };
    window.addEventListener('scroll', onScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', onScrollHandler);
  }, [allowOverrides, manuallyCalculateOffset, stickyStyleBase]);

  if (tabs.length === 0) return null;

  return (
    <Box style={styles.wrapper}>
      {/* @ts-expect-error Sticky doesn't contain `children` in types  */}
      <Sticky
        ref={stickyRef}
        disableCompensation={disableCompensation}
        className={STICKY_BASE_CLASS_NAME}
        stickyClassName={STICKY_STICKIED_CLASS_NAME}
        topOffset={allowOverrides ? topOffset : 0}
        stickyStyle={allowOverrides ? stickyStyle : undefined}>
        <div className="relative bg-white">
          <Tabs
            activeValue={activeTab ?? undefined}
            onActiveValueChange={(value) => setActiveTab(value as T)}
            withHorizontalAutoScroll
            className="bg-white">
            {tabs.map(({ id, text }) => (
              <TabItem key={id} value={id} onClick={() => handleClick(id)}>
                {text}
              </TabItem>
            ))}
          </Tabs>
        </div>
      </Sticky>
    </Box>
  );
}
